// src/components/ui/AuthShell.tsx — shared backdrop for Login / Register / Forgot.
//
// Verbatim port of docs/prototype/auth.jsx lines 90-115. Radial-gradient
// background, decorative steam SVG top-right at 0.12 opacity, then a centred
// scroll column for the form content. Safe-area inset padding so the form
// doesn't crash into iOS notches or home indicator.

import type { CSSProperties, ReactNode } from "react";
import { C, F, layout } from "../../theme";

export interface AuthShellProps {
  children: ReactNode;
}

const wrapStyle: CSSProperties = {
  minHeight: "100vh",
  background: `radial-gradient(circle at 20% 10%, ${C.brassSurf}, ${C.paper} 60%, ${C.paperWarm} 100%)`,
  display: "flex",
  flexDirection: "column",
  fontFamily: F.body,
  position: "relative",
  overflow: "hidden",
  paddingTop: "max(20px, env(safe-area-inset-top))",
  paddingBottom: "max(20px, env(safe-area-inset-bottom))",
  paddingLeft: "max(16px, env(safe-area-inset-left))",
  paddingRight: "max(16px, env(safe-area-inset-right))",
};

const decorStyle: CSSProperties = {
  position: "absolute",
  right: -60,
  top: -20,
  width: 380,
  opacity: 0.12,
  pointerEvents: "none",
};

const innerStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  maxWidth: layout.maxContentPx,
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
};

export const AuthShell = ({ children }: AuthShellProps) => (
  <div style={wrapStyle}>
    <svg viewBox="0 0 400 200" style={decorStyle} aria-hidden="true">
      <g fill="none" stroke={C.brassDk} strokeWidth="1.4">
        <circle cx="60" cy="60" r="16" />
        <circle cx="98" cy="42" r="22" />
        <circle cx="138" cy="50" r="18" />
        <circle cx="178" cy="34" r="26" />
        <circle cx="220" cy="56" r="18" />
      </g>
      <g fill={C.brassDk} fillOpacity="0.6">
        <rect x="40" y="118" width="240" height="30" rx="4" />
        <rect x="64" y="100" width="60" height="20" rx="3" />
        <rect x="134" y="92" width="44" height="28" rx="3" />
        <circle cx="82" cy="158" r="12" />
        <circle cx="132" cy="158" r="12" />
        <circle cx="232" cy="158" r="12" />
      </g>
      <line x1="0" y1="180" x2="400" y2="180" stroke={C.brassDk} strokeOpacity="0.5" strokeWidth="3" />
    </svg>
    <div style={innerStyle}>{children}</div>
  </div>
);
